<template>
	<ion-modal
		ref="modal"
		:is-open="showModal"
		class="create-note-modal"
		@did-dismiss="closeCreateModal"
	>
		<ion-header>
			<ion-toolbar color="branding">
				<ion-title>{{ t("note.add-note") }}</ion-title>
				<ion-buttons slot="end">
					<ion-button @click="dismiss">
						<ion-icon :icon="closeOutline" />
					</ion-button>
				</ion-buttons>
			</ion-toolbar>
		</ion-header>

		<ion-content class="ion-padding">
			<div class="flex flex-col gap-4">
				<PlantingSelectorTrigger v-model="formData.plantingIds" />
				<LocationSection
					ref="locationSectionRef"
					v-model="formData.coordinates"
					:selected-planting-id="selectedPlantingId"
				/>
				<ObservationSection v-model="formData.observations" />
				<div>
					<SectionHeader :title="t('common.photos')" />
					<ImageUpload v-model="formData.photos" multiple />
				</div>
				<DescriptionSection v-model="formData.description" />
				<ActionRequiredSection v-model="formData.isActionRequired" />
				<SgButton
					expand="block"
					:loading="isLoading"
					:disabled="!isValid || isLoading"
					@click="submit"
				>
					{{ t("common.submit") }}
				</SgButton>
			</div>
		</ion-content>
	</ion-modal>
</template>

<script setup lang="ts">
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonButtons,
		IonButton,
		IonContent,
		IonIcon,
	} from "@ionic/vue";
	import { closeOutline } from "ionicons/icons";
	import { useRoute } from "vue-router";
	import { useNoteForm } from "@/components/feature/notes/useNoteForm";
	import DescriptionSection from "@/components/feature/notes/DescriptionSection.vue";
	import LocationSection from "@/components/feature/notes/LocationSection.vue";
	import ObservationSection from "@/components/feature/notes/ObservationSection.vue";
	import PlantingSelectorTrigger from "@/components/feature/notes/PlantingSelectorTrigger.vue";
	import SectionHeader from "@/components/feature/notes/SectionHeader.vue";
	import ImageUpload from "@/components/ui/ImageUpload.vue";
	import { computed, ref, watch, nextTick, onMounted } from "vue";
	import SgButton from "@/components/ui/SgButton.vue";
	import { useModalNavigation } from "@/composables/useModalNavigation";
	import ActionRequiredSection from "@/components/feature/notes/ActionRequiredSection.vue";
	import { useNearestPlanting } from "@/composables/useNearestPlanting";
	import { useI18n } from "vue-i18n";

	const { t } = useI18n();
	const route = useRoute();
	const { closeGlobalModal } = useModalNavigation();
	const { nearestPlanting } = useNearestPlanting();

	const modal = ref<InstanceType<typeof IonModal>>();
	const showModal = computed(() => {
		return route.query.modal === "create-note";
	});

	const closeCreateModal = () => {
		if (showModal.value) {
			resetForm();
			closeGlobalModal();
		}
	};

	const dismiss = () => {
		modal.value?.$el.dismiss();
	};

	const plantingIdFromUrl = computed(() => {
		const plantingId = route.query.plantingId;
		if (plantingId && typeof plantingId === "string") {
			const parsed = parseInt(plantingId, 10);
			return !isNaN(parsed) ? parsed : null;
		}
		return null;
	});

	// Use URL planting ID if available, otherwise use nearest planting
	const preselectedPlantings = computed(() => {
		const urlPlantingId = plantingIdFromUrl.value;
		if (urlPlantingId) {
			return [urlPlantingId];
		}

		// Fallback to nearest planting if available
		if (nearestPlanting.value) {
			return [nearestPlanting.value.id];
		}

		return [];
	});

	const noteForm = useNoteForm({
		initialPlantingIds: [],
	});

	const { isValid, isLoading, submitNote, resetForm, formData } = noteForm;

	// Watch for changes in modal visibility and preselected plantings
	watch([showModal, preselectedPlantings], ([isOpen, plantingIds]) => {
		if (isOpen) {
			formData.plantingIds = [...plantingIds];
		}
	});

	const selectedPlantingId = computed(() =>
		formData.plantingIds.length > 0 ? formData.plantingIds[0] : null,
	);

	const locationSectionRef = ref<InstanceType<typeof LocationSection> | null>(
		null,
	);

	const submit = async () => {
		// Finalize polygon drawing if needed. This is required for the polygon to always be valid
		locationSectionRef.value?.finalizePolygonIfDrawing?.();
		await nextTick(); // Wait for reactivity to update

		await submitNote();
		resetForm();
		closeGlobalModal();
	};

	onMounted(() => {
		if (showModal.value) {
			formData.plantingIds = [...preselectedPlantings.value];
		}
	});
</script>

<style scoped>
	/* Desktop styles */
	@media (min-width: 768px) {
		.create-note-modal {
			--height: 90%;
		}
	}
</style>
